import type { OrgType, Scenario, SettingType } from "../profile/types";

// ── Organization Type Benchmarks ────────────────────────────

interface OrgBenchmark {
  median_encounters: number;
  median_beds: number;
  median_physicians: number;
  median_net_revenue: number;
  avg_revenue_per_encounter: number;
  avg_occupancy: number;
  avg_los: number;
  op_to_ip_ratio: number;
  encounters_per_bed: number; // derived: median_encounters / median_beds
  encounters_per_physician: number; // derived: median_encounters / median_physicians
}

export const ORG_BENCHMARKS: Record<OrgType, OrgBenchmark> = {
  academic_medical_center: {
    median_encounters: 600_000,
    median_beds: 500,
    median_physicians: 250,
    median_net_revenue: 2_500_000_000,
    avg_revenue_per_encounter: 4_200,
    avg_occupancy: 0.75,
    avg_los: 5.5,
    op_to_ip_ratio: 5.0,
    encounters_per_bed: 1_200,
    encounters_per_physician: 2_400,
  },
  community_health_system: {
    median_encounters: 250_000,
    median_beds: 200,
    median_physicians: 100,
    median_net_revenue: 800_000_000,
    avg_revenue_per_encounter: 3_200,
    avg_occupancy: 0.65,
    avg_los: 4.5,
    op_to_ip_ratio: 4.5,
    encounters_per_bed: 1_250,
    encounters_per_physician: 2_500,
  },
  critical_access_hospital: {
    median_encounters: 30_000,
    median_beds: 25,
    median_physicians: 10,
    median_net_revenue: 50_000_000,
    avg_revenue_per_encounter: 1_700,
    avg_occupancy: 0.35,
    avg_los: 3.0,
    op_to_ip_ratio: 6.0,
    encounters_per_bed: 1_200,
    encounters_per_physician: 3_000,
  },
  physician_group: {
    median_encounters: 75_000,
    median_beds: 0,
    median_physicians: 25,
    median_net_revenue: 50_000_000,
    avg_revenue_per_encounter: 650,
    avg_occupancy: 0,
    avg_los: 0,
    op_to_ip_ratio: 0,
    encounters_per_bed: 0,
    encounters_per_physician: 3_000,
  },
  specialty_hospital: {
    median_encounters: 100_000,
    median_beds: 100,
    median_physicians: 60,
    median_net_revenue: 400_000_000,
    avg_revenue_per_encounter: 4_000,
    avg_occupancy: 0.6,
    avg_los: 3.5,
    op_to_ip_ratio: 3.0,
    encounters_per_bed: 1_000,
    encounters_per_physician: 1_667,
  },
  safety_net_hospital: {
    median_encounters: 150_000,
    median_beds: 200,
    median_physicians: 80,
    median_net_revenue: 400_000_000,
    avg_revenue_per_encounter: 2_700,
    avg_occupancy: 0.7,
    avg_los: 5.0,
    op_to_ip_ratio: 4.0,
    encounters_per_bed: 750,
    encounters_per_physician: 1_875,
  },
};

// ── Specialty Definitions ───────────────────────────────────

interface SpecialtyDefinition {
  key: string;
  label: string;
  /** Default weight per org type (sums to ~1.0 per org type) */
  org_weights: Partial<Record<OrgType, number>>;
}

export const SPECIALTY_DEFINITIONS: SpecialtyDefinition[] = [
  {
    key: "primary_care",
    label: "Primary Care / Family Medicine",
    org_weights: {
      academic_medical_center: 0.22,
      community_health_system: 0.3,
      critical_access_hospital: 0.45,
      physician_group: 0.4,
      specialty_hospital: 0.05,
      safety_net_hospital: 0.28,
    },
  },
  {
    key: "internal_medicine",
    label: "Internal Medicine",
    org_weights: {
      academic_medical_center: 0.14,
      community_health_system: 0.15,
      critical_access_hospital: 0.08,
      physician_group: 0.2,
      specialty_hospital: 0.05,
      safety_net_hospital: 0.14,
    },
  },
  {
    key: "cardiology",
    label: "Cardiology",
    org_weights: {
      academic_medical_center: 0.08,
      community_health_system: 0.08,
      critical_access_hospital: 0.05,
      physician_group: 0.1,
      specialty_hospital: 0.15,
      safety_net_hospital: 0.07,
    },
  },
  {
    key: "orthopedics",
    label: "Orthopedics",
    org_weights: {
      academic_medical_center: 0.07,
      community_health_system: 0.08,
      critical_access_hospital: 0.03,
      physician_group: 0.1,
      specialty_hospital: 0.1,
      safety_net_hospital: 0.05,
    },
  },
  {
    key: "surgery",
    label: "Surgery (General)",
    org_weights: {
      academic_medical_center: 0.08,
      community_health_system: 0.07,
      critical_access_hospital: 0.05,
      physician_group: 0.05,
      specialty_hospital: 0.25,
      safety_net_hospital: 0.07,
    },
  },
  {
    key: "radiology",
    label: "Radiology",
    org_weights: {
      academic_medical_center: 0.06,
      community_health_system: 0.05,
      critical_access_hospital: 0.08,
      physician_group: 0.0,
      specialty_hospital: 0.2,
      safety_net_hospital: 0.05,
    },
  },
  {
    key: "oncology",
    label: "Oncology",
    org_weights: {
      academic_medical_center: 0.06,
      community_health_system: 0.03,
      critical_access_hospital: 0.02,
      physician_group: 0.0,
      specialty_hospital: 0.1,
      safety_net_hospital: 0.03,
    },
  },
  {
    key: "neurology",
    label: "Neurology",
    org_weights: {
      academic_medical_center: 0.04,
      community_health_system: 0.03,
      critical_access_hospital: 0.02,
      physician_group: 0.05,
      specialty_hospital: 0.02,
      safety_net_hospital: 0.03,
    },
  },
  {
    key: "emergency_medicine",
    label: "Emergency Medicine",
    org_weights: {
      academic_medical_center: 0.1,
      community_health_system: 0.12,
      critical_access_hospital: 0.15,
      physician_group: 0.0,
      specialty_hospital: 0.03,
      safety_net_hospital: 0.14,
    },
  },
  {
    key: "psychiatry",
    label: "Psychiatry / Behavioral Health",
    org_weights: {
      academic_medical_center: 0.04,
      community_health_system: 0.02,
      critical_access_hospital: 0.02,
      physician_group: 0.0,
      specialty_hospital: 0.0,
      safety_net_hospital: 0.05,
    },
  },
  {
    key: "ob_gyn",
    label: "OB/GYN",
    org_weights: {
      academic_medical_center: 0.04,
      community_health_system: 0.04,
      critical_access_hospital: 0.02,
      physician_group: 0.0,
      specialty_hospital: 0.0,
      safety_net_hospital: 0.04,
    },
  },
  {
    key: "pediatrics",
    label: "Pediatrics",
    org_weights: {
      academic_medical_center: 0.04,
      community_health_system: 0.03,
      critical_access_hospital: 0.03,
      physician_group: 0.0,
      specialty_hospital: 0.0,
      safety_net_hospital: 0.03,
    },
  },
  {
    key: "dermatology",
    label: "Dermatology",
    org_weights: {
      academic_medical_center: 0.01,
      community_health_system: 0.0,
      critical_access_hospital: 0.0,
      physician_group: 0.05,
      specialty_hospital: 0.0,
      safety_net_hospital: 0.01,
    },
  },
  {
    key: "rheumatology",
    label: "Rheumatology",
    org_weights: {
      academic_medical_center: 0.02,
      community_health_system: 0.0,
      critical_access_hospital: 0.0,
      physician_group: 0.05,
      specialty_hospital: 0.05,
      safety_net_hospital: 0.01,
    },
  },
];

// ── Workforce Ratios (per 10,000 encounters) ────────────────

interface WorkforceRatio {
  ftes_per_10k_encounters: number;
  avg_cost: number;
}

/** All workforce roles including scheduling + denial_management (not in base archetypes) */
export const WORKFORCE_RATIOS: Record<
  OrgType,
  Record<string, WorkforceRatio>
> = {
  academic_medical_center: {
    coder: { ftes_per_10k_encounters: 0.52, avg_cost: 70_000 },
    pa_staff: { ftes_per_10k_encounters: 0.26, avg_cost: 55_000 },
    call_center_agent: { ftes_per_10k_encounters: 0.42, avg_cost: 45_000 },
    physician: { ftes_per_10k_encounters: 2.08, avg_cost: 350_000 },
    nurse: { ftes_per_10k_encounters: 8.33, avg_cost: 85_000 },
    scheduling: { ftes_per_10k_encounters: 0.47, avg_cost: 45_000 },
    denial_management: { ftes_per_10k_encounters: 0.13, avg_cost: 65_000 },
    cdi_specialist: { ftes_per_10k_encounters: 0.08, avg_cost: 75_000 },
    eligibility_verification: {
      ftes_per_10k_encounters: 0.15,
      avg_cost: 45_000,
    },
    revenue_integrity: { ftes_per_10k_encounters: 0.1, avg_cost: 65_000 },
    perioperative_admin: { ftes_per_10k_encounters: 0.05, avg_cost: 55_000 },
  },
  community_health_system: {
    coder: { ftes_per_10k_encounters: 0.48, avg_cost: 65_000 },
    pa_staff: { ftes_per_10k_encounters: 0.24, avg_cost: 52_000 },
    call_center_agent: { ftes_per_10k_encounters: 0.38, avg_cost: 42_000 },
    physician: { ftes_per_10k_encounters: 1.9, avg_cost: 300_000 },
    nurse: { ftes_per_10k_encounters: 7.5, avg_cost: 78_000 },
    scheduling: { ftes_per_10k_encounters: 0.45, avg_cost: 42_000 },
    denial_management: { ftes_per_10k_encounters: 0.12, avg_cost: 60_000 },
    cdi_specialist: { ftes_per_10k_encounters: 0.06, avg_cost: 70_000 },
    eligibility_verification: {
      ftes_per_10k_encounters: 0.12,
      avg_cost: 42_000,
    },
    revenue_integrity: { ftes_per_10k_encounters: 0.08, avg_cost: 60_000 },
    perioperative_admin: { ftes_per_10k_encounters: 0.04, avg_cost: 50_000 },
  },
  critical_access_hospital: {
    coder: { ftes_per_10k_encounters: 0.6, avg_cost: 58_000 },
    pa_staff: { ftes_per_10k_encounters: 0.2, avg_cost: 48_000 },
    call_center_agent: { ftes_per_10k_encounters: 0.3, avg_cost: 38_000 },
    physician: { ftes_per_10k_encounters: 2.5, avg_cost: 280_000 },
    nurse: { ftes_per_10k_encounters: 9.0, avg_cost: 72_000 },
    scheduling: { ftes_per_10k_encounters: 0.35, avg_cost: 38_000 },
    denial_management: { ftes_per_10k_encounters: 0.1, avg_cost: 55_000 },
    cdi_specialist: { ftes_per_10k_encounters: 0.04, avg_cost: 65_000 },
    eligibility_verification: {
      ftes_per_10k_encounters: 0.1,
      avg_cost: 38_000,
    },
    revenue_integrity: { ftes_per_10k_encounters: 0.05, avg_cost: 55_000 },
    perioperative_admin: { ftes_per_10k_encounters: 0.02, avg_cost: 45_000 },
  },
  physician_group: {
    coder: { ftes_per_10k_encounters: 0.4, avg_cost: 62_000 },
    pa_staff: { ftes_per_10k_encounters: 0.15, avg_cost: 50_000 },
    call_center_agent: { ftes_per_10k_encounters: 0.35, avg_cost: 40_000 },
    physician: { ftes_per_10k_encounters: 3.0, avg_cost: 320_000 },
    nurse: { ftes_per_10k_encounters: 5.0, avg_cost: 75_000 },
    scheduling: { ftes_per_10k_encounters: 0.5, avg_cost: 40_000 },
    denial_management: { ftes_per_10k_encounters: 0.08, avg_cost: 55_000 },
    cdi_specialist: { ftes_per_10k_encounters: 0.02, avg_cost: 65_000 },
    eligibility_verification: {
      ftes_per_10k_encounters: 0.08,
      avg_cost: 38_000,
    },
    revenue_integrity: { ftes_per_10k_encounters: 0.04, avg_cost: 55_000 },
    perioperative_admin: { ftes_per_10k_encounters: 0.01, avg_cost: 45_000 },
  },
  specialty_hospital: {
    coder: { ftes_per_10k_encounters: 0.55, avg_cost: 72_000 },
    pa_staff: { ftes_per_10k_encounters: 0.28, avg_cost: 56_000 },
    call_center_agent: { ftes_per_10k_encounters: 0.32, avg_cost: 43_000 },
    physician: { ftes_per_10k_encounters: 2.2, avg_cost: 380_000 },
    nurse: { ftes_per_10k_encounters: 7.0, avg_cost: 82_000 },
    scheduling: { ftes_per_10k_encounters: 0.4, avg_cost: 43_000 },
    denial_management: { ftes_per_10k_encounters: 0.11, avg_cost: 62_000 },
    cdi_specialist: { ftes_per_10k_encounters: 0.07, avg_cost: 72_000 },
    eligibility_verification: {
      ftes_per_10k_encounters: 0.12,
      avg_cost: 43_000,
    },
    revenue_integrity: { ftes_per_10k_encounters: 0.09, avg_cost: 63_000 },
    perioperative_admin: { ftes_per_10k_encounters: 0.06, avg_cost: 55_000 },
  },
  safety_net_hospital: {
    coder: { ftes_per_10k_encounters: 0.5, avg_cost: 62_000 },
    pa_staff: { ftes_per_10k_encounters: 0.28, avg_cost: 52_000 },
    call_center_agent: { ftes_per_10k_encounters: 0.4, avg_cost: 42_000 },
    physician: { ftes_per_10k_encounters: 1.8, avg_cost: 290_000 },
    nurse: { ftes_per_10k_encounters: 8.0, avg_cost: 76_000 },
    scheduling: { ftes_per_10k_encounters: 0.45, avg_cost: 42_000 },
    denial_management: { ftes_per_10k_encounters: 0.15, avg_cost: 58_000 },
    cdi_specialist: { ftes_per_10k_encounters: 0.07, avg_cost: 68_000 },
    eligibility_verification: {
      ftes_per_10k_encounters: 0.18,
      avg_cost: 42_000,
    },
    revenue_integrity: { ftes_per_10k_encounters: 0.08, avg_cost: 58_000 },
    perioperative_admin: { ftes_per_10k_encounters: 0.03, avg_cost: 48_000 },
  },
};

// ── Baseline Benchmarks (fixed industry averages) ───────────

export const BASELINE_BENCHMARKS = {
  avg_pa_handling_time_minutes: 25, // CAQH Index
  physician_burnout_rate: 0.45, // Medscape 2024
  avg_call_handle_time_minutes: 6.5,
  call_abandonment_rate: 0.08,
  denial_recovery_rate: 0.55,
  denial_rework_cost: 57.23, // Experian 2025
  cancellation_rate: 0.12, // ~12% of ambulatory appointments cancelled
  current_backfill_rate: 0.3, // ~30% of cancelled slots currently backfilled
  avg_doc_time_per_encounter_minutes: 16,
  coding_accuracy_rate: 0.88,
  pa_approval_rate: 0.85,
  pa_turnaround_hours: 48,
  avg_wrvus_per_physician_per_month: 420,
  // ── CDI Baselines ──────────────────────────────────────
  cdi_review_rate: 0.65, // 65% of discharges reviewed
  documentation_driven_denial_rate: 0.12, // 12% of denials are doc-specificity
  // ── Patient Financial Clearance Baselines ──────────────
  registration_denial_rate: 0.22, // 22% of denials registration-driven
  avg_verification_time_minutes: 12,
  // ── Charge Capture Baselines ───────────────────────────
  missed_charge_rate_surgical: 0.015,
  missed_charge_rate_ed: 0.01,
  missed_charge_rate_outpatient: 0.005,
  charge_lag_days: 3,
  // ── OR Optimization Baselines ──────────────────────────
  block_utilization_rate: 0.7,
  avg_revenue_per_or_hour: 2_000,
} as const;

// ── Org-Type-Specific Benchmarks ──────────────────────────

export const CURRENT_CMI: Record<OrgType, number> = {
  academic_medical_center: 1.8,
  community_health_system: 1.4,
  specialty_hospital: 1.6,
  critical_access_hospital: 1.2,
  physician_group: 1.1,
  safety_net_hospital: 1.5,
};

export const OR_UTILIZATION_RATE: Record<OrgType, number> = {
  academic_medical_center: 0.65,
  community_health_system: 0.62,
  specialty_hospital: 0.68,
  critical_access_hospital: 0.55,
  physician_group: 0.5,
  safety_net_hospital: 0.6,
};

export const SURGICAL_ENCOUNTER_SHARE: Record<OrgType, number> = {
  academic_medical_center: 0.08,
  community_health_system: 0.06,
  specialty_hospital: 0.15,
  critical_access_hospital: 0.04,
  physician_group: 0.02,
  safety_net_hospital: 0.05,
};

// ── Labor Realization Rate ──────────────────────────────

export const LABOR_REALIZATION_RATE: Record<Scenario, number> = {
  conservative: 0.5,
  moderate: 0.65,
  aggressive: 0.85,
};

// ── PA Rates per Encounter (by payer) ───────────────────────

/** Fraction of encounters that require prior authorization, by payer type.
 *  Sources: KFF/OIG (Medicare), CAQH 2025 Index (commercial), state MCO data (Medicaid) */
export const PA_RATE_PER_ENCOUNTER: Record<string, number> = {
  medicare_ffs: 0.002, // OIG: ~400K PAs for ~35M+ enrollees; near-zero
  medicare_advantage: 0.08, // KFF/OIG: ~50M PAs / ~25M enrollees @ ~25 enc/yr
  commercial: 0.05, // CAQH 2025 Index: ~5% of commercial txns require PA
  medicaid: 0.03, // Lower than managed; limited plan-level restrictions
  medicaid_managed: 0.04, // State MCO data; moderate formulary restrictions
  self_pay: 0.0, // No payer authorization required
};

/** PA denial rate by payer (CAQH / KFF sources) */
export const PA_DENIAL_RATE_BY_PAYER: Record<string, number> = {
  medicare_ffs: 0.084,
  medicare_advantage: 0.157,
  commercial: 0.139,
  medicaid: 0.1,
  medicaid_managed: 0.12,
  self_pay: 0.0,
};

// ── Average Revenue per Encounter by Payer ──────────────────

/** Average revenue per encounter by payer type. Sources: MedPAC, HFMA. */
export const AVG_REVENUE_PER_ENCOUNTER_BY_PAYER: Record<string, number> = {
  medicare_ffs: 2800,
  medicare_advantage: 3200,
  commercial: 5200,
  medicaid: 1900,
  medicaid_managed: 2100,
  self_pay: 650, // Collection rate ~30-40% of charges
};

// ── No-Show Rate Multiplier by Payer ────────────────────────

/** No-show rate multiplier by payer type. Sources: MGMA, published literature.
 *  Note: self-pay is moderate (1.2) because it includes both cost-barrier
 *  patients (who no-show more) AND cash-pay elective patients (who no-show less).
 *  For specific practices, the user should provide real data. */
export const NOSHOW_RATE_BY_PAYER: Record<string, number> = {
  medicare_ffs: 0.85,
  medicare_advantage: 0.9,
  commercial: 0.8,
  medicaid: 1.4,
  medicaid_managed: 1.3,
  self_pay: 1.2,
};

// ── Unified Specialty Benchmarks ─────────────────────────────
// Consolidates 9 separate specialty lookup tables into one map.
// Sources: MGMA, CMS physician fee schedule, ACS NSQIP, Medscape 2024,
// AMA, Nuance, AHIMA, AAPC, ACDIS, KFF, ScienceDirect meta-analyses.

export interface SpecialtyBenchmarks {
  noshowRate: number;
  revenueMultiplier: number;
  physicianCost: number;
  codingComplexity: number;
  surgicalFraction: number;
  burnoutRate: number;
  cancellationRate: number;
  docTimeMinutes: number;
  appointmentValue: number;
}

export const SPECIALTY_BENCHMARKS: Record<string, SpecialtyBenchmarks> = {
  primary_care: {
    noshowRate: 0.18,
    revenueMultiplier: 0.85,
    physicianCost: 380_000,
    codingComplexity: 1.0,
    surgicalFraction: 0.0,
    burnoutRate: 0.48,
    cancellationRate: 0.14,
    docTimeMinutes: 16,
    appointmentValue: 180,
  },
  internal_medicine: {
    noshowRate: 0.18,
    revenueMultiplier: 0.95,
    physicianCost: 400_000,
    codingComplexity: 1.1,
    surgicalFraction: 0.005,
    burnoutRate: 0.52,
    cancellationRate: 0.13,
    docTimeMinutes: 18,
    appointmentValue: 220,
  },
  family_medicine: {
    noshowRate: 0.18, // Same as primary_care (no explicit entry in old NOSHOW table)
    revenueMultiplier: 0.85, // Same as primary_care (no explicit entry in old REVENUE table)
    physicianCost: 380_000, // Same as primary_care (no explicit entry in old COST table)
    codingComplexity: 1.0, // Same as primary_care (no explicit entry in old COMPLEXITY table)
    surgicalFraction: 0.0,
    burnoutRate: 0.48,
    cancellationRate: 0.14,
    docTimeMinutes: 15,
    appointmentValue: 165,
  },
  pediatrics: {
    noshowRate: 0.3,
    revenueMultiplier: 0.75,
    physicianCost: 340_000,
    codingComplexity: 0.8,
    surgicalFraction: 0.005,
    burnoutRate: 0.46,
    cancellationRate: 0.15,
    docTimeMinutes: 14,
    appointmentValue: 160,
  },
  cardiology: {
    noshowRate: 0.15,
    revenueMultiplier: 1.45,
    physicianCost: 700_000,
    codingComplexity: 1.35,
    surgicalFraction: 0.06,
    burnoutRate: 0.42,
    cancellationRate: 0.1,
    docTimeMinutes: 22,
    appointmentValue: 450,
  },
  orthopedics: {
    noshowRate: 0.14,
    revenueMultiplier: 1.4,
    physicianCost: 680_000,
    codingComplexity: 1.25,
    surgicalFraction: 0.15,
    burnoutRate: 0.4,
    cancellationRate: 0.09,
    docTimeMinutes: 14,
    appointmentValue: 400,
  },
  surgery: {
    noshowRate: 0.1,
    revenueMultiplier: 1.35,
    physicianCost: 650_000,
    codingComplexity: 1.5,
    surgicalFraction: 0.35,
    burnoutRate: 0.43,
    cancellationRate: 0.06,
    docTimeMinutes: 20,
    appointmentValue: 500,
  },
  neurology: {
    noshowRate: 0.16,
    revenueMultiplier: 1.2,
    physicianCost: 500_000,
    codingComplexity: 1.3,
    surgicalFraction: 0.02,
    burnoutRate: 0.5,
    cancellationRate: 0.12,
    docTimeMinutes: 24,
    appointmentValue: 350,
  },
  oncology: {
    noshowRate: 0.08,
    revenueMultiplier: 1.3,
    physicianCost: 600_000,
    codingComplexity: 1.4,
    surgicalFraction: 0.04,
    burnoutRate: 0.49,
    cancellationRate: 0.05,
    docTimeMinutes: 25,
    appointmentValue: 600,
  },
  gastroenterology: {
    noshowRate: 0.18, // No explicit entry in old NOSHOW table; use default
    revenueMultiplier: 1.0, // No explicit entry in old REVENUE table; use default
    physicianCost: 400_000, // No explicit entry in old COST table; use default
    codingComplexity: 1.0, // No explicit entry in old COMPLEXITY table; use default
    surgicalFraction: 0.12,
    burnoutRate: 0.41,
    cancellationRate: 0.08,
    docTimeMinutes: 18,
    appointmentValue: 250, // No explicit entry in old APPOINTMENT table; use default
  },
  pulmonology: {
    noshowRate: 0.18, // No explicit entry in old NOSHOW table; use default
    revenueMultiplier: 1.0, // No explicit entry in old REVENUE table; use default
    physicianCost: 400_000, // No explicit entry in old COST table; use default
    codingComplexity: 1.0, // No explicit entry in old COMPLEXITY table; use default
    surgicalFraction: 0.02,
    burnoutRate: 0.45,
    cancellationRate: 0.11,
    docTimeMinutes: 20,
    appointmentValue: 250, // No explicit entry in old APPOINTMENT table; use default
  },
  emergency_medicine: {
    noshowRate: 0.0,
    revenueMultiplier: 1.05,
    physicianCost: 480_000,
    codingComplexity: 0.95,
    surgicalFraction: 0.01,
    burnoutRate: 0.65,
    cancellationRate: 0.0,
    docTimeMinutes: 12,
    appointmentValue: 550,
  },
  radiology: {
    noshowRate: 0.12,
    revenueMultiplier: 1.15,
    physicianCost: 550_000,
    codingComplexity: 0.9,
    surgicalFraction: 0.0,
    burnoutRate: 0.38,
    cancellationRate: 0.1,
    docTimeMinutes: 8,
    appointmentValue: 280,
  },
  psychiatry: {
    noshowRate: 0.25,
    revenueMultiplier: 0.55,
    physicianCost: 360_000,
    codingComplexity: 0.85,
    surgicalFraction: 0.0,
    burnoutRate: 0.47,
    cancellationRate: 0.18,
    docTimeMinutes: 30,
    appointmentValue: 200,
  },
  ob_gyn: {
    noshowRate: 0.16,
    revenueMultiplier: 1.0,
    physicianCost: 450_000,
    codingComplexity: 1.05,
    surgicalFraction: 0.15,
    burnoutRate: 0.53,
    cancellationRate: 0.11,
    docTimeMinutes: 16,
    appointmentValue: 280,
  },
  dermatology: {
    noshowRate: 0.3,
    revenueMultiplier: 0.8,
    physicianCost: 500_000,
    codingComplexity: 1.0, // No explicit entry in old COMPLEXITY table; use default
    surgicalFraction: 0.03,
    burnoutRate: 0.32,
    cancellationRate: 0.15,
    docTimeMinutes: 10,
    appointmentValue: 250,
  },
  rheumatology: {
    noshowRate: 0.15,
    revenueMultiplier: 1.1,
    physicianCost: 420_000,
    codingComplexity: 1.0, // No explicit entry in old COMPLEXITY table; use default
    surgicalFraction: 0.0,
    burnoutRate: 0.39,
    cancellationRate: 0.12,
    docTimeMinutes: 20,
    appointmentValue: 300,
  },
  other: {
    noshowRate: 0.18,
    revenueMultiplier: 1.0,
    physicianCost: 400_000,
    codingComplexity: 1.0,
    surgicalFraction: 0.02,
    burnoutRate: 0.45,
    cancellationRate: 0.12,
    docTimeMinutes: 16,
    appointmentValue: 250,
  },
  _default: {
    noshowRate: 0.18,
    revenueMultiplier: 1.0,
    physicianCost: 400_000,
    codingComplexity: 1.0,
    surgicalFraction: 0.02,
    burnoutRate: 0.45,
    cancellationRate: 0.12,
    docTimeMinutes: 16,
    appointmentValue: 250,
  },
};

// ── Derived backward-compat views ───────────────────────────
// These are generated from SPECIALTY_BENCHMARKS so existing imports don't break.

export const SPECIALTY_NOSHOW_RATE: Record<string, number> = Object.fromEntries(
  Object.entries(SPECIALTY_BENCHMARKS).map(([k, v]) => [k, v.noshowRate]),
);

// ── Coding Time by Care Setting ─────────────────────────────

export const CODING_TIME_MINUTES: Record<string, number> = {
  inpatient: 20,
  outpatient: 10,
  emergency: 8,
  ambulatory: 8,
  radiology: 4,
};

// ============================================================================
// V3: PAYER-SPECIALTY-AWARE REFERENCE DATA
// ============================================================================

// ── Payer Reimbursement Index ─────────────────────────────────
// Relative reimbursement rate vs. system-wide average (1.0).
// Sources: MedPAC, KFF, CMS cost reports.
export const PAYER_REIMBURSEMENT_INDEX: Record<string, number> = {
  commercial: 1.35,
  workers_comp: 0.95,
  medicare_advantage: 0.87,
  medicare_ffs: 0.82,
  medicaid_managed: 0.52,
  medicaid: 0.48,
  self_pay: 0.18,
};

// ── Specialty Revenue Multiplier (derived from SPECIALTY_BENCHMARKS) ──
export const SPECIALTY_REVENUE_MULTIPLIER: Record<string, number> =
  Object.fromEntries(
    Object.entries(SPECIALTY_BENCHMARKS).map(([k, v]) => [
      k,
      v.revenueMultiplier,
    ]),
  );

// ── Payer Denial Rate ─────────────────────────────────────────
// Overall claim denial rate by payer type.
// Sources: KFF, Kodiak Solutions, Experian Health 2025.
export const PAYER_DENIAL_RATE: Record<string, number> = {
  medicare_advantage: 0.147,
  commercial: 0.139,
  medicaid_managed: 0.095,
  medicare_ffs: 0.084,
  medicaid: 0.078,
  self_pay: 0.0,
  workers_comp: 0.06,
};

// ── Payer No-Show Multiplier ──────────────────────────────────
// Relative no-show propensity vs. system average (1.0).
// Sources: ScienceDirect meta-analyses, JAMA Internal Medicine.
export const PAYER_NOSHOW_MULTIPLIER: Record<string, number> = {
  medicaid: 1.4,
  medicaid_managed: 1.35,
  self_pay: 1.35,
  commercial: 0.9,
  medicare_ffs: 0.8,
  medicare_advantage: 0.8,
  workers_comp: 0.85,
};

// ── Specialty Physician Cost (derived from SPECIALTY_BENCHMARKS) ──
export const SPECIALTY_PHYSICIAN_COST: Record<string, number> =
  Object.fromEntries(
    Object.entries(SPECIALTY_BENCHMARKS).map(([k, v]) => [k, v.physicianCost]),
  );

// ── Inpatient Coding Complexity (derived from SPECIALTY_BENCHMARKS) ──
export const INPATIENT_CODING_COMPLEXITY: Record<string, number> =
  Object.fromEntries(
    Object.entries(SPECIALTY_BENCHMARKS).map(([k, v]) => [
      k,
      v.codingComplexity,
    ]),
  );

// ── Coverage Discovery Payer Mix ──────────────────────────────
// Of patients flagged as self-pay who actually have coverage,
// which payers are discovered? Medicaid managed dominates.
// Sources: Experian Health, Waystar coverage discovery data.
export const COVERAGE_DISCOVERY_PAYER_MIX: Record<string, number> = {
  medicaid_managed: 0.4,
  commercial: 0.18,
  medicaid: 0.15,
  medicare_advantage: 0.12,
  medicare_ffs: 0.08,
  workers_comp: 0.05,
  self_pay: 0.02, // Residual: charity care, state programs
};

// ── Self-Pay Setting Mix ──────────────────────────────────────
// Where do self-pay encounters concentrate?
// Sources: CMS MEDPAR, AHA Annual Survey.
export const SELF_PAY_SETTING_MIX: Record<string, number> = {
  emergency: 0.45,
  outpatient: 0.25,
  inpatient: 0.2,
  ambulatory: 0.1,
};

// ── PA Abandonment Risk by Payer ──────────────────────────────
// Fraction of PA-required encounters where patients abandon care
// due to PA delays. Sources: AMA, KFF patient surveys.
export const PA_ABANDONMENT_RISK_BY_PAYER: Record<string, number> = {
  medicare_advantage: 0.15,
  commercial: 0.1,
  medicaid_managed: 0.08,
  medicaid: 0.06,
  medicare_ffs: 0.02,
  self_pay: 0.0,
  workers_comp: 0.05,
};

// ── Contribution Margin by Setting ────────────────────────────
// Net margin on retained/recovered revenue by care setting.
// Sources: HFMA, Kaufman Hall benchmarks.
export const CONTRIBUTION_MARGIN_BY_SETTING: Record<string, number> = {
  imaging: 0.5,
  outpatient: 0.45,
  surgical: 0.4,
  ambulatory: 0.4,
  inpatient: 0.3,
  emergency: 0.25,
};

// ── Surgical Payer Adjustment ─────────────────────────────────
// Relative revenue yield for incremental surgical cases by payer.
// Sources: MedPAC, hospital cost reports.
export const SURGICAL_PAYER_ADJUSTMENT: Record<string, number> = {
  commercial: 1.15,
  workers_comp: 1.05,
  medicare_ffs: 1.05,
  medicare_advantage: 1.0,
  medicaid_managed: 0.65,
  medicaid: 0.6,
  self_pay: 0.5,
};

// ── QA Review Rate ────────────────────────────────────────────
// What fraction of autonomously coded encounters require human
// QA review? Declines as system matures.
export const QA_REVIEW_RATE: Record<string, number> = {
  ramp: 0.2,
  conservative: 0.1,
  moderate: 0.07,
  aggressive: 0.05,
};

// ── CMI Peer Benchmark ────────────────────────────────────────
// Expected CMI by org type for durability factor calculation.
// Sources: CMS Inpatient PPS, ACDIS benchmarks.
export const CMI_PEER_BENCHMARK: Record<OrgType, number> = {
  academic_medical_center: 1.85,
  specialty_hospital: 1.65,
  safety_net_hospital: 1.55,
  community_health_system: 1.45,
  critical_access_hospital: 1.25,
  physician_group: 1.1,
};

// ── Geographic Wage Index (by US State) ─────────────────────────
// CMS Geographic Practice Cost Index (GPCI) work component,
// normalized so national average = 1.0.
// Sources: CMS GPCI 2025, BLS Occupational Employment & Wage Statistics.
export const STATE_WAGE_INDEX: Record<string, number> = {
  AL: 0.87,
  AK: 1.22,
  AZ: 0.95,
  AR: 0.85,
  CA: 1.18,
  CO: 1.03,
  CT: 1.14,
  DE: 1.02,
  FL: 0.96,
  GA: 0.93,
  HI: 1.08,
  ID: 0.88,
  IL: 1.01,
  IN: 0.91,
  IA: 0.89,
  KS: 0.9,
  KY: 0.88,
  LA: 0.89,
  ME: 0.95,
  MD: 1.08,
  MA: 1.16,
  MI: 0.97,
  MN: 1.0,
  MS: 0.84,
  MO: 0.91,
  MT: 0.89,
  NE: 0.9,
  NV: 1.01,
  NH: 1.02,
  NJ: 1.12,
  NM: 0.91,
  NY: 1.15,
  NC: 0.93,
  ND: 0.9,
  OH: 0.95,
  OK: 0.87,
  OR: 1.03,
  PA: 1.0,
  RI: 1.07,
  SC: 0.9,
  SD: 0.88,
  TN: 0.9,
  TX: 0.96,
  UT: 0.94,
  VT: 0.98,
  VA: 1.01,
  WA: 1.1,
  WV: 0.87,
  WI: 0.95,
  WY: 0.91,
  DC: 1.2,
};

// ── State-Level Payer Mix Adjustments ─────────────────────────────
// Multipliers applied to archetype payer mix shares, then re-normalized.
// Sources: KFF State Health Facts, CMS Geographic Variation reports.
export const STATE_PAYER_MIX_ADJUSTMENTS: Record<
  string,
  Partial<Record<string, number>>
> = {
  // Northeast — higher commercial, lower Medicaid
  NY: { commercial: 1.15, medicaid: 1.2, medicare_ffs: 0.95 },
  NJ: { commercial: 1.2, medicaid: 0.9 },
  CT: { commercial: 1.25, medicaid: 0.85 },
  MA: { commercial: 1.15, medicaid: 1.1, self_pay: 0.6 }, // MA has near-universal coverage
  PA: { commercial: 1.1 },

  // Southeast — higher Medicaid, lower commercial
  MS: { medicaid: 1.4, commercial: 0.8, self_pay: 1.3 },
  AL: { medicaid: 1.3, commercial: 0.85, self_pay: 1.2 },
  LA: { medicaid: 1.35, commercial: 0.85 },
  GA: { medicaid: 1.15, commercial: 0.9, self_pay: 1.15 },
  SC: { medicaid: 1.2, commercial: 0.9 },

  // Southwest — higher self-pay, higher Medicaid
  TX: { self_pay: 1.4, medicaid: 1.1, commercial: 0.9 },
  AZ: { self_pay: 1.2, medicaid: 1.1 },
  NM: { self_pay: 1.3, medicaid: 1.3, commercial: 0.75 },

  // Midwest — relatively balanced
  OH: { commercial: 1.05 },
  IL: { commercial: 1.1, medicaid: 1.05 },
  MI: { medicaid: 1.1, commercial: 0.95 },
  MN: { commercial: 1.15, self_pay: 0.7 },

  // West
  CA: { commercial: 1.1, medicaid: 1.15, self_pay: 0.9 },
  WA: { commercial: 1.15, self_pay: 0.8 },
  OR: { commercial: 1.1, medicaid: 1.1, self_pay: 0.85 },
  CO: { commercial: 1.15, self_pay: 0.85 },

  // Florida — high Medicare
  FL: { medicare_ffs: 1.25, medicare_advantage: 1.3, commercial: 0.85 },

  // High uninsured
  WY: { self_pay: 1.3, commercial: 0.85 },
  OK: { self_pay: 1.25, medicaid: 1.1, commercial: 0.85 },
};

// ── Setting-Type Adjustments ────────────────────────────────────
// Multipliers applied on top of state wage index for urban/rural/suburban.
// Also adjusts no-show rates and encounter volume expectations.
// Sources: HRSA, AHA Rural Health, CMS Area Wage Index.
export const SETTING_TYPE_ADJUSTMENTS: Record<
  SettingType,
  {
    wage_multiplier: number;
    noshow_multiplier: number;
    encounter_multiplier: number;
    payer_adjustments: Partial<Record<string, number>>;
    denial_rate_multiplier: number;
  }
> = {
  urban: {
    wage_multiplier: 1.08,
    noshow_multiplier: 1.1,
    encounter_multiplier: 1.05,
    payer_adjustments: { commercial: 1.1, self_pay: 1.15, medicaid: 1.05 },
    denial_rate_multiplier: 1.05, // Urban providers face slightly higher denial rates (more complex claims)
  },
  suburban: {
    wage_multiplier: 1.0,
    noshow_multiplier: 1.0,
    encounter_multiplier: 1.0,
    payer_adjustments: {},
    denial_rate_multiplier: 1.0,
  },
  rural: {
    wage_multiplier: 0.88,
    noshow_multiplier: 0.9,
    encounter_multiplier: 0.9,
    payer_adjustments: { medicare_ffs: 1.15, self_pay: 1.2, commercial: 0.8 },
    denial_rate_multiplier: 0.92, // Rural providers tend to have simpler claim profiles, lower denial rates
  },
};

// ── Teaching Status Adjustments ──────────────────────────────────
// Teaching hospitals have higher costs, higher CMI, more complex cases.
// Sources: MedPAC, AAMC Teaching Hospital Data.
export const TEACHING_ADJUSTMENTS = {
  wage_multiplier: 1.06, // IME/GME cost uplift
  cmi_uplift: 0.15, // Teaching adds ~0.15 to CMI
  coding_complexity: 1.12, // More complex documentation
  payer_adjustments: {
    medicaid: 1.2,
    medicare_ffs: 1.1,
    commercial: 0.9,
  } as Record<string, number>, // Teaching hospitals serve more Medicaid/Medicare
  denial_rate_multiplier: 1.08, // Higher claim complexity → more denials
} as const;

// ── US States (for dropdown) ─────────────────────────────────────
export const US_STATES: { value: string; label: string }[] = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "DC", label: "District of Columbia" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
];

// ── EHR Systems (for dropdown) ──────────────────────────────────
export const EHR_SYSTEMS: { value: string; label: string }[] = [
  { value: "epic", label: "Epic" },
  { value: "cerner", label: "Cerner / Oracle Health" },
  { value: "meditech", label: "MEDITECH" },
  { value: "athenahealth", label: "athenahealth" },
  { value: "eclinicalworks", label: "eClinicalWorks" },
  { value: "allscripts", label: "Allscripts / Veradigm" },
  { value: "nextgen", label: "NextGen Healthcare" },
  { value: "cpsi", label: "CPSI / TruBridge" },
  { value: "modernizing_medicine", label: "Modernizing Medicine" },
  { value: "other", label: "Other" },
];

// ── Deployed AI Solution Options (for tech stack UI) ────────────
export interface TechStackCategory {
  id: string;
  label: string;
  options: { value: string; label: string }[];
}

export const TECH_STACK_CATEGORIES: TechStackCategory[] = [
  {
    id: "coding",
    label: "Autonomous Coding / CAC",
    options: [
      { value: "fathom", label: "Fathom" },
      { value: "3m_codeassist", label: "3M CodeAssist" },
      { value: "optum_cac", label: "Optum CAC" },
      { value: "nym_health", label: "Nym Health" },
      { value: "coding_other", label: "Other coding AI" },
    ],
  },
  {
    id: "prior_auth",
    label: "Prior Authorization",
    options: [
      { value: "humata_pa", label: "Humata Health" },
      { value: "covermymeds", label: "CoverMyMeds" },
      { value: "cohere_health", label: "Cohere Health" },
      { value: "waystar_pa", label: "Waystar" },
      { value: "pa_other", label: "Other PA automation" },
    ],
  },
  {
    id: "ambient",
    label: "Ambient Documentation",
    options: [
      { value: "nuance_dax", label: "Nuance DAX / DAX Copilot" },
      { value: "abridge", label: "Abridge" },
      { value: "deepscribe", label: "DeepScribe" },
      { value: "suki", label: "Suki" },
      { value: "ambient_other", label: "Other ambient AI" },
    ],
  },
  {
    id: "scheduling",
    label: "Scheduling / Patient Access",
    options: [
      { value: "queuedr", label: "QueueDr" },
      { value: "relatient", label: "Relatient / Phreesia" },
      { value: "scheduling_other", label: "Other scheduling AI" },
    ],
  },
  {
    id: "cdi",
    label: "Clinical Documentation Integrity",
    options: [
      { value: "3m_360_encompass", label: "3M 360 Encompass" },
      { value: "optum_cdi", label: "Optum CDI" },
      { value: "iodine", label: "Iodine Software" },
      { value: "cdi_other", label: "Other CDI" },
    ],
  },
  {
    id: "denial_mgmt",
    label: "Denial Management",
    options: [
      { value: "experian_claim", label: "Experian ClaimSource" },
      { value: "change_healthcare", label: "Change Healthcare" },
      { value: "denial_other", label: "Other denial management" },
    ],
  },
];

// ── Call Resolution Rate ──────────────────────────────────────
// Fraction of deflected calls fully resolved without callback.
// Sources: Parlance, Hyro vendor data; no peer-reviewed studies.
export const CALL_RESOLUTION_RATE: Record<string, number> = {
  conservative: 0.7,
  moderate: 0.8,
  aggressive: 0.9,
};
